import React, { useState } from "react";
import { X } from "lucide-react";

export default function CreateChannelModal({ isOpen, onClose, users, currentUserEmail, onCreate }) {
  if (!isOpen) return null;

  const [channelName, setChannelName] = useState("");
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [error, setError] = useState("");

  const capitalizeName = (name) => {
    if (!name) return "";
    return name.split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  };

  const getFormattedName = (u) => {
    if (!u) return "";
    return capitalizeName(u.displayName || u.name || u.email?.split("@")[0] || "User");
  };

  // Filter other users (excluding soft-deleted/disabled users)
  const otherUsers = users.filter(
    u => u.email?.toLowerCase() !== currentUserEmail?.toLowerCase() && !u.deleted && u.status !== "Disabled"
  );

  const handleToggleUser = (email) => {
    const lowerEmail = email.toLowerCase();
    setSelectedEmails(prev => 
      prev.includes(lowerEmail) 
        ? prev.filter(e => e !== lowerEmail) 
        : [...prev, lowerEmail]
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");

    const name = channelName.trim().toLowerCase().replace(/[^a-z0-9\s-_.]/g, "").replace(/\s+/g, "-");
    if (!name) {
      setError("Please enter a valid channel name.");
      return;
    }

    // Always include creator
    const finalInvited = [currentUserEmail.toLowerCase(), ...selectedEmails];

    onCreate(name, finalInvited);
    setChannelName("");
    setSelectedEmails([]);
    onClose();
  };

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.75)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1100,
      backdropFilter: "blur(4px)"
    }}>
      <div 
        className="card glass-card animate-scale-up" 
        style={{
          width: "480px",
          maxWidth: "90%",
          height: "70vh",
          maxHeight: "600px",
          display: "flex",
          flexDirection: "column",
          padding: 0,
          border: "1px solid var(--border-color)",
          backgroundColor: "rgba(20, 24, 33, 0.95)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.5)"
        }}
      >
        {/* Header */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "1rem 1.5rem",
          borderBottom: "1px solid var(--border-color)",
          backgroundColor: "rgba(10, 12, 18, 0.4)"
        }}>
          <h3 style={{ margin: 0, color: "var(--primary)", fontSize: "1.1rem", fontWeight: "700" }}>
            Create Channel
          </h3>
          <button 
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "var(--text-secondary)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ padding: "1.5rem", display: "flex", flexDirection: "column", gap: "1.25rem", flex: 1, overflowY: "auto" }}>
            {error && (
              <div style={{ color: "#e74c3c", fontSize: "0.78rem", backgroundColor: "rgba(231,76,60,0.1)", padding: "0.5rem", borderRadius: "4px" }}>
                {error}
              </div>
            )}

            {/* Channel Name */}
            <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
              <label style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.6)", fontWeight: "600" }}>
                Channel Name
              </label>
              <input
                type="text"
                placeholder="e.g. project-x-planning"
                value={channelName}
                onChange={(e) => setChannelName(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.55rem 0.75rem",
                  fontSize: "0.82rem",
                  borderRadius: "4px",
                  border: "1px solid var(--border-color)",
                  backgroundColor: "var(--bg-input)",
                  color: "var(--text-primary)",
                  outline: "none"
                }}
                required
              />
            </div>

            {/* Invite Members */}
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", flex: 1, overflowY: "hidden" }}>
              <label style={{ fontSize: "0.75rem", color: "var(--text-secondary)", fontWeight: "600" }}>
                Invite Members (Optional)
              </label>
              <p style={{ margin: 0, fontSize: "0.7rem", color: "var(--text-secondary)" }}>
                Selected users will be able to see and join this channel. If none are selected, the channel will be public.
              </p>
              
              <div style={{
                flex: 1,
                overflowY: "auto",
                border: "1px solid var(--border-color)",
                borderRadius: "4px",
                backgroundColor: "rgba(10, 12, 18, 0.2)",
                padding: "0.5rem"
              }}>
                {otherUsers.map((u) => {
                  const fullName = getFormattedName(u);
                  const isChecked = selectedEmails.includes(u.email.toLowerCase());
                  return (
                    <div
                      key={u.id || u.email}
                      onClick={() => handleToggleUser(u.email)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.75rem",
                        padding: "0.45rem 0.6rem",
                        borderRadius: "4px",
                        cursor: "pointer",
                        backgroundColor: isChecked ? "rgba(255,255,255,0.02)" : "transparent",
                        marginBottom: "0.2rem"
                      }}
                      className="slack-item-hover"
                    >
                      <input 
                        type="checkbox" 
                        checked={isChecked}
                        onChange={() => {}} // handled by row click
                        style={{ cursor: "pointer" }}
                      />
                      <div style={{
                        width: "24px",
                        height: "24px",
                        borderRadius: "3px",
                        backgroundColor: u.role === "Admin" ? "#e74c3c" : "#3498db",
                        color: "#ffffff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "700",
                        fontSize: "0.7rem"
                      }}>
                        {fullName.substring(0, 1).toUpperCase()}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                        <span style={{ fontSize: "0.78rem", color: "var(--text-primary)", fontWeight: "600", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {fullName}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Form Footer */}
          <div style={{
            padding: "1rem 1.5rem",
            borderTop: "1px solid var(--border-color)",
            backgroundColor: "rgba(10, 12, 18, 0.4)",
            display: "flex",
            justifyContent: "flex-end",
            gap: "0.75rem"
          }}>
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={onClose}
              style={{ padding: "0.45rem 1rem", fontSize: "0.8rem" }}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              style={{ padding: "0.45rem 1.25rem", fontSize: "0.8rem" }}
            >
              Create Channel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
